/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DocumentResponse,
  PersonaStructure,
  BofuTabObject,
  BofuTabDocument,
  BofuTabStatus,
  BofuTabPropietario,
  GarantiasVehicularInmobiliariaResponse,
  BofuTabRepresentante,
  BofuSlice,
  BofuAvalAdicionalBuroInfo,
  PersonTypesConyugeForm,
  PersonTypeIdentityValidation,
  BofuTabFormsEmpresa,
} from "@interfaces/response";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UUID } from "crypto";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";

export interface TransactionalInfo {
  payment_methods: Array<UUID>;
  details: {
    id: UUID | undefined | string;
    advance_payments: boolean | undefined;
    own_resources: boolean | undefined;
    pep_shareholder: boolean | undefined;
    pep_members: boolean | undefined;
  };
}

const initialState: BofuSlice = {
  owner_id: null,
  information_representante_id: null,
  aval_adicional_id_information: null,
  aval_adicional_id_bank_information: null,
  isLoading: false,
  representante_legal: undefined,
  aval: undefined,
  aval_conyuge: undefined,
  avales_adicionales: [],
  aval_adicional_buro_info: null,
  aval_regimen_conyugal: undefined,
  bofu_tabs_obj: {
    dictamen: {
      code: BOFU_TABS_CONFIG.tab_dictamen.code,
      url: BOFU_TABS_CONFIG.tab_dictamen.url,
      order: 1,
      is_active: true,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
    },
    empresa: {
      code: BOFU_TABS_CONFIG.tab_empresa.code,
      url: BOFU_TABS_CONFIG.tab_empresa.url,
      order: 2,
      is_active: true,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      forms: {
        solicitud_credito: {
          enabled: true,
          config: {},
        },
        referencias_personales: true,
        informacion_bancaria: true,
      },
      doc_solicitud_credito: undefined,
    },
    aval: {
      code: BOFU_TABS_CONFIG.tab_aval.code,
      url: BOFU_TABS_CONFIG.tab_aval.url,
      order: 3,
      is_active: true,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      forms: {
        informacion_aval: true,
        validacion_identidad: true,
        informacion_bancaria: true,
      },
      documents_conyuge: [],
      doc_anexo_1: undefined,
      doc_validacion_identidad: undefined,
      show_form_aval: false,
      doc_anexo_1_conyuge: undefined,
      doc_validacion_identidad_conyuge: undefined,
      show_information_form_conyuge: false,
    },
    propietarios_reales: {
      code: BOFU_TABS_CONFIG.tab_propietario.code,
      url: BOFU_TABS_CONFIG.tab_propietario.url,
      order: 4,
      is_active: true,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      propietarios: [],
    },
    garantia: {
      code: BOFU_TABS_CONFIG.tab_garantia.code,
      url: BOFU_TABS_CONFIG.tab_garantia.url,
      order: 5,
      is_active: true,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      items: [],
    },
    representantes: {
      code: BOFU_TABS_CONFIG.tab_representante.code,
      url: BOFU_TABS_CONFIG.tab_representante.url,
      order: 6,
      is_active: false,
      is_disabled: false,
      tab_status: undefined,
      documents: [],
      forms: {
        informacion_representante_legal: true,
        validacion_identidad: true,
        informacion_bancaria: true,
      },
      items: [],
    },
  },
  params_identity_validation_form: {
    person_id: undefined,
    person_type: undefined,
  },
  params_conyuge_form: {
    person_id: undefined,
    person_type: undefined,
  },
};

export const bofuSlice = createSlice({
  name: "bofu",
  initialState,
  reducers: {
    setCurrentOwnerId: (state, action: PayloadAction<string>) => {
      state.owner_id = action.payload;
    },
    setInformationRepresentanteId: (state, action: PayloadAction<string>) => {
      state.information_representante_id = action.payload;
    },
    setAvalAdicionalIdInformation: (state, action: PayloadAction<string>) => {
      state.aval_adicional_id_information = action.payload;
    },
    setAvalAdicionalIdBankInformation: (state, action: PayloadAction<string>) => {
      state.aval_adicional_id_bank_information = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setRepresentanteLegal: (state, action: PayloadAction<PersonaStructure | undefined>) => {
      state.representante_legal = action.payload;
    },
    setAval: (state, action: PayloadAction<PersonaStructure | undefined>) => {
      state.aval = action.payload;
    },
    setAvalConyuge: (state, action: PayloadAction<PersonaStructure | undefined>) => {
      state.aval_conyuge = action.payload;
    },
    setAvalRegimenConyugal: (state, action: PayloadAction<string | undefined>) => {
      state.aval_regimen_conyugal = action.payload;
    },
    setAvalesAdicionales: (state, action: PayloadAction<BofuTabRepresentante[]>) => {
      state.avales_adicionales = action.payload;
    },
    setAvalAdicionalBuroInfo: (state, action: PayloadAction<Partial<BofuAvalAdicionalBuroInfo>>) => {
      if (state.aval_adicional_buro_info) {
        state.aval_adicional_buro_info = {
          ...state.aval_adicional_buro_info,
          ...action.payload,
        };
      } else {
        state.aval_adicional_buro_info = action.payload as BofuAvalAdicionalBuroInfo;
      }
    },
    setBofuStatusAll: (
      state,
      action: PayloadAction<{
        status_dictamen: BofuTabStatus | undefined;
        status_empresa: BofuTabStatus | undefined;
        status_aval: BofuTabStatus | undefined;
        status_propietarios_reales: BofuTabStatus | undefined;
        status_garantia: BofuTabStatus | undefined;
        status_representante: BofuTabStatus | undefined;
      }>,
    ) => {
      state.bofu_tabs_obj.dictamen.tab_status = action.payload.status_dictamen;
      state.bofu_tabs_obj.empresa.tab_status = action.payload.status_empresa;
      state.bofu_tabs_obj.aval.tab_status = action.payload.status_aval;
      state.bofu_tabs_obj.propietarios_reales.tab_status = action.payload.status_propietarios_reales;
      state.bofu_tabs_obj.garantia.tab_status = action.payload.status_garantia;
      state.bofu_tabs_obj.representantes.tab_status = action.payload.status_representante;
    },
    setBofuTabsObj: (state, action: PayloadAction<BofuTabObject>) => {
      state.bofu_tabs_obj = action.payload;
    },
    // Usado en flujos (como PF) que no pasan por TabBofu y por lo tanto no llenan
    // bofu_tabs_obj completo: permite guardar solo la config de "empresa.forms" (la que
    // useSolicitudCreditoForm lee) desde una consulta directa a getTabsConfig.
    setBofuTabEmpresaForms: (state, action: PayloadAction<BofuTabFormsEmpresa>) => {
      state.bofu_tabs_obj.empresa.forms = action.payload;
    },
    setBofuTabDictamenDocument: (
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) => {
      const { document, newValue, valueAction, valueIndex } = action.payload;
      const documentIndex = state.bofu_tabs_obj.dictamen.documents.findIndex(
        (doc) => doc.file_category_code === document.file_category_code && doc.relationship === document.relationship,
      );

      if (documentIndex !== -1) {
        if (document.multiple) {
          if (valueAction === "add") {
            state.bofu_tabs_obj.dictamen.documents[documentIndex].list_values.push(newValue);
          } else if (valueAction === "update") {
            if (valueIndex !== undefined) {
              state.bofu_tabs_obj.dictamen.documents[documentIndex].list_values[valueIndex] = newValue;
            }
          } else if (valueAction === "remove") {
            if (valueIndex !== undefined) {
              state.bofu_tabs_obj.dictamen.documents[documentIndex].list_values.splice(valueIndex, 1);
            }
          }
        } else {
          state.bofu_tabs_obj.dictamen.documents[documentIndex].file_value = newValue;
        }
      }
    },
    setBofuTabEmpresaDocument: (
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) => {
      const { document, newValue, valueAction, valueIndex } = action.payload;
      const documentIndex = state.bofu_tabs_obj.empresa.documents.findIndex(
        (doc) => doc.file_category_code === document.file_category_code && doc.relationship === document.relationship,
      );

      if (documentIndex !== -1) {
        if (document.multiple) {
          if (valueAction === "add") {
            state.bofu_tabs_obj.empresa.documents[documentIndex].list_values.push(newValue);
          } else if (valueAction === "update") {
            if (valueIndex !== undefined) {
              state.bofu_tabs_obj.empresa.documents[documentIndex].list_values[valueIndex] = newValue;
            }
          } else if (valueAction === "remove") {
            if (valueIndex !== undefined) {
              state.bofu_tabs_obj.empresa.documents[documentIndex].list_values.splice(valueIndex, 1);
            }
          }
        } else {
          state.bofu_tabs_obj.empresa.documents[documentIndex].file_value = newValue;
        }
      }
    },
    setBofuTabAvalDocument: (
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
      }>,
    ) => {
      if (state.bofu_tabs_obj.aval) {
        if (action.payload.document.relationship === "conyuge_aval") {
          state.bofu_tabs_obj.aval.documents_conyuge.forEach((doc) => {
            if (doc.file_category_code === action.payload.document.file_category_code) {
              doc.file_value = action.payload.newValue;
            }
          });
        } else {
          state.bofu_tabs_obj.aval.documents.forEach((doc) => {
            if (doc.file_category_code === action.payload.document.file_category_code) {
              doc.file_value = action.payload.newValue;
            }
          });
        }
      }
    },
    setBofuTabPropietarioDocument: (
      state,
      action: PayloadAction<{
        persona: PersonaStructure;
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update";
        valueIndex?: number;
      }>,
    ) => {
      const { persona, document, newValue, valueAction, valueIndex } = action.payload;
      const personIndex = state.bofu_tabs_obj.propietarios_reales.propietarios.findIndex(
        (propietarioItem: BofuTabPropietario) => propietarioItem.persona.id === persona.id,
      );

      if (personIndex !== -1) {
        const personItem = state.bofu_tabs_obj.propietarios_reales.propietarios[personIndex];

        const documentIndex = personItem.documents.findIndex(
          (doc) => doc.file_category_code === document.file_category_code,
        );

        if (documentIndex !== -1) {
          if (document.multiple) {
            if (valueAction === "add") {
              personItem.documents[documentIndex].list_values.push(newValue);
            } else if (valueAction === "update") {
              if (valueIndex !== undefined) {
                personItem.documents[documentIndex].list_values[valueIndex] = newValue;
              }
            } else if (valueAction === "remove") {
              if (valueIndex !== undefined) {
                personItem.documents[documentIndex].list_values.splice(valueIndex, 1);
              }
            }
          } else {
            personItem.documents[documentIndex].file_value = newValue;
          }
        }

        state.bofu_tabs_obj.propietarios_reales.propietarios[personIndex] = personItem;
      }
    },
    setBofuTabGarantiaDocument: (
      state,
      action: PayloadAction<{
        guarantee: GarantiasVehicularInmobiliariaResponse;
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) => {
      const { guarantee, document, newValue, valueAction, valueIndex } = action.payload;
      const guaranteeIndex = state.bofu_tabs_obj.garantia.items.findIndex((item) => item.guarantee.id === guarantee.id);

      if (guaranteeIndex !== -1) {
        const guaranteeItem = state.bofu_tabs_obj.garantia.items[guaranteeIndex];

        const documentIndex = guaranteeItem.documents.findIndex(
          (doc) => doc.file_category_code === document.file_category_code,
        );

        if (documentIndex !== -1) {
          if (document.multiple) {
            if (valueAction === "add") {
              guaranteeItem.documents[documentIndex].list_values.push(newValue);
            } else if (valueAction === "update") {
              if (valueIndex !== undefined) {
                guaranteeItem.documents[documentIndex].list_values[valueIndex] = newValue;
              }
            } else if (valueAction === "remove") {
              if (valueIndex !== undefined) {
                guaranteeItem.documents[documentIndex].list_values.splice(valueIndex, 1);
              }
            }
          } else {
            guaranteeItem.documents[documentIndex].file_value = newValue;
          }
        }
      }
    },
    setBofuTabRepresentanteDocument: (
      state,
      action: PayloadAction<{
        persona: PersonaStructure;
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) => {
      const { persona, document, newValue, valueAction, valueIndex } = action.payload;
      const personIndex = state.bofu_tabs_obj.representantes.items.findIndex(
        (representanteItem: BofuTabRepresentante) => representanteItem.persona.id === persona.id,
      );

      if (personIndex !== -1) {
        const personItem = state.bofu_tabs_obj.representantes.items[personIndex];
        const documentIndex = personItem.documents.findIndex(
          (doc) => doc.file_category_code === document.file_category_code,
        );
        if (documentIndex !== -1) {
          if (document.multiple) {
            if (valueAction === "add") {
              personItem.documents[documentIndex].list_values.push(newValue);
            } else if (valueAction === "update") {
              if (valueIndex !== undefined) {
                personItem.documents[documentIndex].list_values[valueIndex] = newValue;
              }
            } else if (valueAction === "remove") {
              if (valueIndex !== undefined) {
                personItem.documents[documentIndex].list_values.splice(valueIndex, 1);
              }
            }
          } else {
            personItem.documents[documentIndex].file_value = newValue;
          }
        }
      }
    },
    setBofuTabAvalAdicionalDocument: (
      state,
      action: PayloadAction<{
        persona: PersonaStructure;
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) => {
      const { persona, document, newValue, valueAction, valueIndex } = action.payload;
      const personIndex = state.avales_adicionales.findIndex(
        (avalAdicional) => avalAdicional.persona.id === persona.id,
      );

      if (personIndex !== -1) {
        const personItem = state.avales_adicionales[personIndex];
        const documentIndex = personItem.documents.findIndex(
          (doc) => doc.file_category_code === document.file_category_code,
        );
        if (documentIndex !== -1) {
          if (document.multiple) {
            if (valueAction === "add") {
              personItem.documents[documentIndex].list_values.push(newValue);
            } else if (valueAction === "update") {
              if (valueIndex !== undefined) {
                personItem.documents[documentIndex].list_values[valueIndex] = newValue;
              }
            } else if (valueAction === "remove") {
              if (valueIndex !== undefined) {
                personItem.documents[documentIndex].list_values.splice(valueIndex, 1);
              }
            }
          } else {
            personItem.documents[documentIndex].file_value = newValue;
          }
        }
      }
    },
    setBofuTabAvalAdicionalConyugeDocument: (
      state,
      action: PayloadAction<{
        persona: PersonaStructure | undefined;
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) => {
      const { persona, document, newValue, valueAction, valueIndex } = action.payload;

      if (!persona) return;

      const avalAdicionalIndex = state.avales_adicionales.findIndex(
        (avalAdicional) => avalAdicional.conyuge?.persona.id === persona.id,
      );

      if (avalAdicionalIndex !== -1) {
        const personItem = state.avales_adicionales[avalAdicionalIndex];

        // validar que exista el conyuge del aval adicional
        if (!personItem.conyuge) return;

        // buscar el indice del documento en el conyuge del aval adicional|
        const documentIndex = personItem.conyuge.documents.findIndex(
          (doc) => doc.file_category_code === document.file_category_code,
        );

        if (documentIndex !== -1) {
          if (document.multiple) {
            if (valueAction === "add") {
              personItem.conyuge?.documents[documentIndex].list_values.push(newValue);
            } else if (valueAction === "update") {
              if (valueIndex !== undefined) {
                personItem.conyuge.documents[documentIndex].list_values[valueIndex] = newValue;
              }
            } else if (valueAction === "remove") {
              if (valueIndex !== undefined) {
                personItem.conyuge.documents[documentIndex].list_values.splice(valueIndex, 1);
              }
            }
          } else {
            personItem.conyuge.documents[documentIndex].file_value = newValue;
          }
        }
      }
    },
    setParamsIdentityValidationForm: (
      state,
      action: PayloadAction<{ person_id?: string; person_type?: PersonTypeIdentityValidation }>,
    ) => {
      state.params_identity_validation_form = action.payload;
    },
    setParamsConyugeForm: (
      state,
      action: PayloadAction<{ person_id?: string; person_type?: PersonTypesConyugeForm }>,
    ) => {
      state.params_conyuge_form = action.payload;
    },
  },
});

export const {
  setCurrentOwnerId,
  setInformationRepresentanteId,
  setAvalAdicionalIdInformation,
  setAvalAdicionalIdBankInformation,
  setIsLoading,
  setRepresentanteLegal,
  setAval,
  setAvalConyuge,
  setAvalRegimenConyugal,
  setAvalesAdicionales,
  setAvalAdicionalBuroInfo,
  setBofuStatusAll,
  setBofuTabsObj,
  setBofuTabEmpresaForms,
  setBofuTabDictamenDocument,
  setBofuTabEmpresaDocument,
  setBofuTabAvalDocument,
  setBofuTabPropietarioDocument,
  setBofuTabGarantiaDocument,
  setBofuTabRepresentanteDocument,
  setBofuTabAvalAdicionalDocument,
  setBofuTabAvalAdicionalConyugeDocument,
  setParamsIdentityValidationForm,
  setParamsConyugeForm,
} = bofuSlice.actions;
export default bofuSlice.reducer;
